import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown, ShieldCheck, Sparkles, Wrench } from "lucide-react";
import heroRoof from "@/assets/hero-roof.webp";

const HeroSection = () => {
  const [scrollY, setScrollY] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    let rafId = 0;
    let ticking = false;
    // Cache the section height once; only matters that we stop updating once the
    // hero is well off-screen. Recompute on resize.
    let sectionHeight = sectionRef.current?.offsetHeight ?? window.innerHeight;
    const recomputeHeight = () => {
      sectionHeight = sectionRef.current?.offsetHeight ?? window.innerHeight;
    };

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      rafId = window.requestAnimationFrame(() => {
        const y = window.scrollY;
        if (y < sectionHeight) {
          setScrollY(y);
        }
        ticking = false;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", recomputeHeight, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", recomputeHeight);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative min-h-screen flex items-center justify-center bg-dark overflow-hidden">
      {/* Colorful hero photo background */}
      <img
        src={heroRoof}
        alt="אור בגג – גגן רעפים מומלץ בחיפה והקריות, תיקון גגות, איטום ופחחות"
        width={1920}
        height={1080}
        loading="eager"
        // @ts-expect-error fetchpriority is a valid HTML attribute not yet typed
        fetchpriority="high"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ transform: `translateY(${scrollY * 0.2}px) scale(1.05)` }}
      />
      {/* Dark overlay to keep text readable */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-dark/80 via-dark/60 to-dark/85"
        aria-hidden="true"
      />

      {/* Animated gold grid */}
      <div className="absolute inset-0 bg-grid-gold opacity-30" aria-hidden="true" />

      {/* Animated gradient glow */}
      <div
        className="absolute inset-0 opacity-60"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(60% 60% at 50% 40%, hsl(43 76% 52% / 0.18) 0%, transparent 70%)",
          animation: "gradient-shift 12s ease-in-out infinite",
          backgroundSize: "200% 200%",
        }}
      />

      {/* Parallax diagonal lines */}
      <div
        className="absolute inset-0 opacity-20"
        aria-hidden="true"
        style={{ transform: `translateY(${scrollY * 0.3}px)` }}
      >
        <div className="absolute inset-0" style={{
          backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 35px, hsl(43 76% 52% / 0.08) 35px, hsl(43 76% 52% / 0.08) 70px)"
        }} />
      </div>

      {/* Floating orbs */}
      <div
        className="absolute top-20 right-10 w-64 h-64 rounded-full bg-primary/10 blur-3xl animate-float-slow"
        aria-hidden="true"
      />
      <div
        className="absolute bottom-24 left-10 w-48 h-48 rounded-full bg-primary/10 blur-2xl animate-float"
        aria-hidden="true"
        style={{ animationDelay: "1.5s" }}
      />

      {/* Floating service icons */}
      <div
        className="hidden md:flex absolute top-32 left-[12%] w-14 h-14 rounded-2xl bg-primary/15 backdrop-blur-sm border border-primary/30 items-center justify-center text-primary animate-float"
        aria-hidden="true"
      >
        <ShieldCheck size={26} />
      </div>
      <div
        className="hidden md:flex absolute bottom-40 right-[10%] w-14 h-14 rounded-2xl bg-primary/15 backdrop-blur-sm border border-primary/30 items-center justify-center text-primary animate-float-slow"
        aria-hidden="true"
        style={{ animationDelay: "0.6s" }}
      >
        <Wrench size={24} />
      </div>
      <div
        className="hidden lg:flex absolute top-1/2 right-[8%] w-12 h-12 rounded-2xl bg-primary/15 backdrop-blur-sm border border-primary/30 items-center justify-center text-primary animate-float"
        aria-hidden="true"
        style={{ animationDelay: "1.2s" }}
      >
        <Sparkles size={22} />
      </div>

      <div
        className="container mx-auto px-4 text-center relative z-10"
        style={{ transform: `translateY(${scrollY * 0.1}px)`, opacity: Math.max(0, 1 - scrollY / 700) }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-primary/10 border border-primary/30 text-primary text-sm font-medium animate-fade-in">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
            13 שנות ניסיון • אחריות מלאה
          </div>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-dark-foreground mb-6 leading-tight animate-fade-in-up">
            אור בגג –{" "}
            <span className="text-gradient-gold">התחנה האחרונה</span>
            <br />
            לבעיות הגג שלך
          </h1>
          <p
            className="text-lg md:text-xl text-dark-foreground/90 mb-8 max-w-2xl mx-auto leading-relaxed animate-fade-in-up"
            style={{ animationDelay: "150ms" }}
          >
            עם למעלה מ-<strong className="text-primary">13 שנות ניסיון</strong> בתחום הגגות והפחחות,
            אנחנו מבטיחים ביצוע ברמת <strong className="text-primary">95%+</strong> ופתרון מקצועי לכל בעיה.
          </p>
          <div
            className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up"
            style={{ animationDelay: "300ms" }}
          >
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-gold-light text-lg px-8 py-6 font-bold transition-transform hover:scale-105 animate-pulse-glow">
              <a href="#contact">תיאום ביקור מקצועי</a>
            </Button>
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-gold-light text-lg px-8 py-6 font-bold transition-transform hover:scale-105">
              <a href="#services">לשירותים שלנו</a>
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <a href="#about" className="absolute bottom-8 left-1/2 -translate-x-1/2 text-primary animate-bounce" aria-label="גלול למטה">
        <ChevronDown size={32} />
      </a>
    </section>
  );
};

export default HeroSection;
